import { useMemo } from "react"
import { cn } from "../../lib/utils"
import {
  getToolLoadingRenderer,
  getToolRenderer,
  type ToolInvocation,
  type ToolRendererOptions,
} from "./tool-registry"
import { useToolRendererRegistry } from "../../contexts/tool-renderer-registry"
import type { ToolCall } from "./types"

interface ToolCallCardProps {
  toolCall: ToolCall
  rendererOptions?: ToolRendererOptions
  className?: string
}

function toInvocation(tc: ToolCall): ToolInvocation {
  const stateMap: Record<ToolCall["state"], ToolInvocation["state"]> = {
    partial: "partial-call",
    call: "call",
    result: "result",
    error: "result",
  }
  return {
    toolCallId: tc.id,
    toolName: tc.name,
    args: tc.args,
    state: stateMap[tc.state],
    result: tc.result,
  }
}

/**
 * Looks up a renderer for the tool by name. While the call is still
 * streaming, prefer the loading renderer (if registered). On result,
 * use the result renderer; if neither is registered, fall back to a
 * generic JSON preview.
 */
export function ToolCallCard({
  toolCall,
  rendererOptions,
  className,
}: ToolCallCardProps) {
  const inv = useMemo(() => toInvocation(toolCall), [toolCall])
  const isFinal = toolCall.state === "result" || toolCall.state === "error"

  // Context-supplied registry takes precedence over the module
  // singleton; falls back to the singleton when no provider is in
  // the tree (back-compat with the existing `registerTool(...)` API).
  const ctx = useToolRendererRegistry()
  const lookupRenderer = (name: string) =>
    ctx?.renderers[name] ?? getToolRenderer(name)
  const lookupLoadingRenderer = (name: string) =>
    ctx?.loadingRenderers[name] ?? getToolLoadingRenderer(name)
  const effectiveOptions = rendererOptions ?? ctx?.options

  if (!isFinal) {
    const loadingRenderer = lookupLoadingRenderer(toolCall.name)
    if (loadingRenderer) {
      return (
        <div data-slot="tool-call-card" className={className}>
          {loadingRenderer(inv)}
        </div>
      )
    }
    return (
      <div
        data-slot="tool-call-card-loading"
        className={cn(
          "flex items-center gap-2 rounded-md border border-dashed border-black/10 bg-foreground/[0.02] px-3 py-2 text-xs text-muted-foreground dark:border-white/10",
          className,
        )}
      >
        <span className="size-1.5 animate-pulse rounded-full bg-primary" />
        <span className="truncate">Calling {toolCall.name}…</span>
      </div>
    )
  }

  if (toolCall.state === "error") {
    return (
      <div
        data-slot="tool-call-card-error"
        className={cn(
          "rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive",
          className,
        )}
      >
        {toolCall.error ?? `Tool ${toolCall.name} failed`}
      </div>
    )
  }

  const renderer = lookupRenderer(toolCall.name)
  if (renderer) {
    const node = renderer(inv, effectiveOptions)
    if (node) {
      return (
        <div data-slot="tool-call-card" className={className}>
          {node}
        </div>
      )
    }
  }

  return (
    <details
      data-slot="tool-call-card-fallback"
      className={cn(
        "rounded-md border border-black/10 bg-foreground/[0.02] px-3 py-2 text-xs dark:border-white/10",
        className,
      )}
    >
      <summary className="cursor-pointer truncate text-muted-foreground">
        {toolCall.name}
      </summary>
      <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-all text-[0.7rem] text-muted-foreground">
        {JSON.stringify(toolCall.result, null, 2)}
      </pre>
    </details>
  )
}
